import { IconMSIContentCopy } from "@assets";
import { UsernameBadge } from "@components/data-display";
import BackButton from "@components/inputs/BackButton";
import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer/MemberSettingsRenderer";
import useAuthSession from "@hooks/useAuthSession";
import { useMemberSettings } from "@hooks/useMemberSettings";
import {
  CopyWrapper,
  List,
  localizePhoneNumber,
  MemberSettingAuthMode,
  MemberSettingNameMode,
  PageTitle,
} from "kz-ui-sdk";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const PersonalInfoPage = () => {
  const { t } = useTranslation();
  const { self, credential } = useAuthSession();
  const { phonePrefix } = useMemberSettings();

  return (
    <div className="flex flex-col">
      <PageTitle
        label={t("personal info")}
        backButton={<BackButton />}
      />
      <List className="mt-4 gap-3 divide-stroke-base bg-grdMid py-4">
        {/* Username section - different based on auth mode */}
        <MemberSettingsRenderer
          feature="authMode"
          match={MemberSettingAuthMode.UsernamePassword}
        >
          <div className="px-4">
            <h4 className="mb-1 text-xs text-content-soft">{t("username")}</h4>
            <CopyWrapper
              text={credential?.accessKey || ""}
              onSuccess={() => {
                toast.success(t("username copied"));
              }}
              onError={() => {
                toast.error(t("failed to copy username"));
              }}
              className="group w-full bg-transparent bg-none !p-0"
            >
              <div className="flex w-full items-center justify-between gap-2 transition-opacity active:opacity-70">
                <p className="text-base font-bold text-content-base">{credential?.accessKey}</p>
                <IconMSIContentCopy className="scale-125 text-content-base transition-transform group-active:!scale-110" />
              </div>
            </CopyWrapper>
          </div>
        </MemberSettingsRenderer>

        <MemberSettingsRenderer
          feature="authMode"
          match={MemberSettingAuthMode.PhonePin}
        >
          <div className="px-4">
            <h4 className="mb-1 text-xs text-content-soft">{t("username")}</h4>
            <UsernameBadge
              className="w-full"
              textClassName="!text-base font-bold text-content-base"
              innerClassName="!p-0 w-full justify-between"
              copyWrapperClassName="!p-0 w-full"
              iconClassName="!text-content-base"
            />
          </div>
        </MemberSettingsRenderer>

        {/* Full name section - different label based on name mode */}
        {!!self?.member?.fullname && (
          <div className="px-4 pt-3">
            <h4 className="mb-1 text-xs text-content-soft">
              <MemberSettingsRenderer
                feature="nameMode"
                match={MemberSettingNameMode.FirstLast}
              >
                {t("first and last name")}
              </MemberSettingsRenderer>
              <MemberSettingsRenderer
                feature="nameMode"
                matchOr={[MemberSettingNameMode.FullInFirst, MemberSettingNameMode.FirstMiddleLast]}
              >
                {t("full name")}
              </MemberSettingsRenderer>
            </h4>
            <p className="text-base font-bold text-content-base">{self?.member?.fullname}</p>
          </div>
        )}

        {/* Phone number section - common to both */}
        <div className="px-4 pt-3">
          <h4 className="mb-1 text-xs text-content-soft">{t("phone number")}</h4>
          <p className="text-base font-bold text-content-base">
            {localizePhoneNumber(self?.member?.phone || "", phonePrefix)}
          </p>
        </div>

        {/* Player ID section - only for PH */}
        <MemberSettingsRenderer
          feature="authMode"
          match={MemberSettingAuthMode.UsernamePassword}
        >
          <div className="px-4 pt-3">
            <h4 className="mb-1 text-xs text-content-soft">{t("player id")}</h4>
            <UsernameBadge
              className="w-full"
              textClassName="!text-base font-bold text-content-base"
              innerClassName="!p-0 w-full justify-between"
              copyWrapperClassName="!p-0 w-full"
              iconClassName="!text-content-base"
            />
          </div>
        </MemberSettingsRenderer>
      </List>
      <div className="py-4 text-center text-[10px] leading-3 text-content-soft">
        {t("please contact customer support for changes.")}
      </div>
    </div>
  );
};

export default PersonalInfoPage;
